"use client";

import Script from "next/script";
import { FormEvent, useEffect, useId, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchPublicForm, submitPublicForm } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PublicFormRendererProps {
  slug: string;
  headingLevel?: 1 | 2;
  showHeading?: boolean;
  className?: string;
}

export function PublicFormRenderer({
  slug,
  headingLevel = 2,
  showHeading = true,
  className = "",
}: PublicFormRendererProps) {
  const router = useRouter();
  const instancePrefix = useId().replace(/[^A-Za-z0-9_-]/g, "");
  const hcaptchaCallbacks = useMemo(
    () => ({
      verify: `onWpCmsHcaptchaVerify${instancePrefix}`,
      expired: `onWpCmsHcaptchaExpired${instancePrefix}`,
    }),
    [instancePrefix],
  );
  const { data: form, isLoading } = useQuery({
    queryKey: ["public-form", slug],
    queryFn: () => fetchPublicForm(slug),
    enabled: Boolean(slug),
  });
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const [message, setMessage] = useState<string | null>(null);
  const hcaptchaSiteKey = process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY;
  const mutation = useMutation({
    mutationFn: () => submitPublicForm(slug, answers, captchaToken),
    onSuccess: (result) => {
      trackEvent("form_submit", { formSlug: slug });
      setMessage(result.successMessage);
      setCaptchaToken(undefined);
      if (result.redirectUrl) router.push(result.redirectUrl);
    },
  });

  useEffect(() => {
    const callbackWindow = window as unknown as Record<string, ((token: string) => void) | (() => void) | undefined>;
    callbackWindow[hcaptchaCallbacks.verify] = (token: string) => setCaptchaToken(token);
    callbackWindow[hcaptchaCallbacks.expired] = () => setCaptchaToken(undefined);
    return () => {
      delete callbackWindow[hcaptchaCallbacks.verify];
      delete callbackWindow[hcaptchaCallbacks.expired];
    };
  }, [hcaptchaCallbacks.expired, hcaptchaCallbacks.verify]);

  if (!slug) {
    return <p className="text-sm text-slate-600">Select a form to display here.</p>;
  }
  if (isLoading) return <p className="text-sm text-slate-600">Loading form...</p>;
  if (!form) return <p className="text-sm text-slate-600">Form not found.</p>;

  const Heading = headingLevel === 1 ? "h1" : "h2";
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (form.hcaptchaEnabled && hcaptchaSiteKey && !captchaToken) {
      setMessage("Please complete the verification challenge.");
      return;
    }
    mutation.mutate();
  };

  return (
    <div className={className}>
      {showHeading && (
        <Heading className={headingLevel === 1 ? "text-3xl font-bold" : "text-2xl font-bold"}>
          {form.name}
        </Heading>
      )}
      {showHeading && form.description && <p className="mt-2 text-slate-600">{form.description}</p>}
      <form onSubmit={submit} className="mt-8 space-y-5">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          value={(answers[form.honeypotField] as string) || ""}
          onChange={(event) => setAnswers((prev) => ({ ...prev, [form.honeypotField]: event.target.value }))}
          aria-hidden="true"
        />
        {form.fields.map((field) => {
          const fieldId = `${instancePrefix}-${field.key}`;
          return (
            <div key={field.key}>
              <Label htmlFor={fieldId}>{field.label}</Label>
              {field.type === "textarea" ? (
                <textarea
                  id={fieldId}
                  required={field.required}
                  className="min-h-32 w-full rounded-[var(--border-radius)] border border-slate-300 px-3 py-2 text-sm"
                  onChange={(event) => setAnswers((prev) => ({ ...prev, [field.key]: event.target.value }))}
                />
              ) : field.type === "select" ? (
                <select
                  id={fieldId}
                  required={field.required}
                  className="min-h-11 w-full rounded-[var(--border-radius)] border border-slate-300 px-3 py-2 text-sm"
                  onChange={(event) => setAnswers((prev) => ({ ...prev, [field.key]: event.target.value }))}
                >
                  <option value="">Select...</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : field.type === "checkbox" ? (
                <label className="mt-2 flex min-h-11 items-center gap-2 text-sm">
                  <input
                    id={fieldId}
                    type="checkbox"
                    required={field.required}
                    onChange={(event) => setAnswers((prev) => ({ ...prev, [field.key]: event.target.checked }))}
                  />
                  {field.label}
                </label>
              ) : (
                <Input
                  id={fieldId}
                  type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
                  required={field.required}
                  onChange={(event) => setAnswers((prev) => ({ ...prev, [field.key]: event.target.value }))}
                />
              )}
            </div>
          );
        })}
        {form.hcaptchaEnabled && hcaptchaSiteKey && (
          <>
            <Script id="hcaptcha-script" src="https://js.hcaptcha.com/1/api.js" async defer />
            <div
              className="h-captcha"
              data-sitekey={hcaptchaSiteKey}
              data-callback={hcaptchaCallbacks.verify}
              data-expired-callback={hcaptchaCallbacks.expired}
            />
          </>
        )}
        {form.hcaptchaEnabled && !hcaptchaSiteKey && (
          <p className="text-sm font-medium text-red-700">Verification is not configured.</p>
        )}
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "Sending..." : "Submit"}
        </Button>
        <div aria-live="polite" aria-atomic="true">
          {message && <p className="text-sm font-medium text-green-700">{message}</p>}
          {mutation.isError && (
            <p className="text-sm font-medium text-red-700">
              {mutation.error instanceof Error ? mutation.error.message : "Submission failed."}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
